import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface PrettyContentProps {
  content: string;
}

const isImageUrl = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;
  const trimmed = str.trim();
  // Check if it's a URL ending with image extension
  return /^https?:\/\/.+\.(png|jpg|jpeg|gif|webp|svg)(\?.*)?$/i.test(trimmed);
};

export const PrettyContent: React.FC<PrettyContentProps> = ({ content }) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollToField = (fieldId: string) => {
    const element = document.getElementById(fieldId);
    if (element && scrollContainerRef.current) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderPrettyContent = (data: any, depth = 0): JSX.Element => {
    if (typeof data === 'string') {
      // Check if the entire value is an image URL (not embedded in other content)
      if (isImageUrl(data.trim()) && data.trim() === data) {
        return (
          <div className="space-y-2">
            <a href={data} target="_blank" rel="noopener noreferrer" className="block">
              <img
                src={data}
                alt="Output image"
                className="max-w-full max-h-64 rounded-lg border border-zinc-200 dark:border-zinc-700 hover:opacity-90 transition-opacity"
              />
            </a>
            <a
              href={data}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline break-all"
            >
              {data}
            </a>
          </div>
        );
      }

      // Check if it's a markdown-like string or just plain text
      if (data.includes('\n') || data.includes('#') || data.includes('*') || data.includes('[')) {
        const MAX_LENGTH = 50000;
        const truncatedData =
          data.length > MAX_LENGTH
            ? data.substring(0, MAX_LENGTH) + '...\n\n_Content truncated. Switch to Raw mode._'
            : data;

        return (
          <div className="prose prose-zinc dark:prose-invert max-w-none break-words prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-zinc-600 dark:prose-p:text-zinc-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-li:text-zinc-600 dark:prose-li:text-zinc-300 prose-pre:whitespace-pre-wrap prose-pre:break-words prose-code:break-all">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{truncatedData}</ReactMarkdown>
          </div>
        );
      }
      return <span className="text-zinc-700 dark:text-zinc-300">{data}</span>;
    }

    if (Array.isArray(data)) {
      return renderArray(data, depth);
    }

    if (typeof data === 'object' && data !== null) {
      return renderObject(data, depth);
    }

    return <span className="text-zinc-700 dark:text-zinc-300">{String(data)}</span>;
  };

  const renderObject = (obj: Record<string, any>, depth: number): JSX.Element => {
    return (
      <div
        className={cn(
          'space-y-6',
          depth > 0 && 'pl-4 border-l-2 border-zinc-100 dark:border-zinc-800'
        )}
      >
        {Object.entries(obj).map(([key, value]) => (
          <div key={key} id={depth === 0 ? `field-${key}` : undefined} className="space-y-3">
            <div
              className={cn(
                'inline-block px-2 py-1 rounded-md text-xs font-semibold uppercase tracking-wider',
                depth === 0
                  ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                  : 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200'
              )}
            >
              {formatKey(key)}
            </div>
            <div>{renderPrettyContent(value, depth + 1)}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderArray = (arr: any[], depth: number): JSX.Element => {
    return (
      <div className="space-y-4">
        {arr.map((item, index) => (
          <div
            key={index}
            className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-800"
          >
            <div className="text-xs font-medium text-zinc-400 dark:text-zinc-500 mb-2 uppercase tracking-wide">
              Item {index + 1}
            </div>
            <div>{renderPrettyContent(item, depth + 1)}</div>
          </div>
        ))}
      </div>
    );
  };

  const formatKey = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .trim();
  };

  let parsedContent;
  try {
    parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
  } catch {
    parsedContent = content;
  }

  // Extract top-level keys for TOC
  const topLevelKeys =
    typeof parsedContent === 'object' && parsedContent !== null && !Array.isArray(parsedContent)
      ? Object.keys(parsedContent)
      : [];

  const showToc = topLevelKeys.length > 2;

  return (
    <div className="h-full w-full min-w-0 flex flex-col overflow-hidden">
      {showToc && (
        <div className="flex-shrink-0 px-6 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 sticky top-0 z-10">
          <div className="flex flex-wrap gap-2">
            {topLevelKeys.map((key) => (
              <button
                key={key}
                onClick={() => scrollToField(`field-${key}`)}
                className="px-2 py-1 text-xs font-medium text-zinc-600 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded transition-colors"
              >
                {formatKey(key)}
              </button>
            ))}
          </div>
        </div>
      )}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-6"
      >
        {renderPrettyContent(parsedContent)}
      </div>
    </div>
  );
};
